import * as Sentry from '@sentry/browser';
import EmptyMessage from '../common/EmptyMessage.jsx';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import Loading from '@components/common/LoadingMessage';
import React, {useEffect, useState} from 'react';
import ReservationCard from './ReservationCard.jsx';
import {
  Alert,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import {
  MESSAGES_KEYS,
  getValue as getMessageSeen,
  setValue as setMessageSeen,
} from '@app/util/local-messages';
import {QUOTA_MANAGER_MODES} from '@app/constants/quotaManager';
import {array, bool, func, number, object, string} from 'prop-types';
import {useFlags} from 'launchdarkly-react-client-sdk';
import {useTranslation} from 'react-i18next';

const CompletedTabContent = ({
  reservations,
  loadingReservations,
  productCount,
  products,
  features,
  loadingFeatures,
  selectedFeatureRefs,
  setSelectedFeatureRefs,
  setZoomedFeature,
  mode,
  collectionId,
}) => {
  const {t} = useTranslation();
  const {hideQuotaModalCtas} = useFlags();
  const hideSubsDelayMsg = getMessageSeen(
    MESSAGES_KEYS.SUBS_CREATION_DELAY_MESSAGE
  );
  const [checked, setChecked] = useState(false);
  const [showAlert, setShowAlert] = useState();

  useEffect(() => {
    if (mode === QUOTA_MANAGER_MODES.PREVIEW && !hideSubsDelayMsg) {
      setShowAlert(true);
    }
  }, [hideSubsDelayMsg, mode]);

  if (loadingReservations) {
    return <Loading suffix="reservations" />;
  }

  if (!productCount && !loadingReservations) {
    return (
      <EmptyMessage
        height="100%"
        icon={<Inventory2OutlinedIcon />}
        title={t('completedTabContent.emptyMsgTitle')}
        description={t('completedTabContent.emptyMsgDesc', {
          actionText:
            mode === QUOTA_MANAGER_MODES.PREVIEW
              ? 'preview quota costs'
              : 'start quota reservation',
        })}
      />
    );
  }

  const handleClose = () => {
    setShowAlert(false);
    if (checked) {
      setMessageSeen(MESSAGES_KEYS.SUBS_CREATION_DELAY_MESSAGE, true);
    }
  };

  return (
    <Stack direction="column" spacing={2}>
      {showAlert && !hideQuotaModalCtas && (
        <Alert severity="info" onClose={handleClose}>
          <Typography variant="body2" gutterBottom>
            {t('completedTabContent.alertDesc')}
          </Typography>
          <FormControlLabel
            size="small"
            label={
              <Typography variant="caption">
                {t('common:noShowCheckboxLabel')}
              </Typography>
            }
            control={
              <Checkbox
                checked={checked}
                onChange={() => setChecked(!checked)}
                sx={{marginLeft: 0.5}}
              />
            }
          />
        </Alert>
      )}
      {Object.keys(reservations).map(productId => {
        const product = products.find(p => {
          return p.id === Number(productId);
        });
        if (!product) {
          Sentry.captureMessage(`Product not found for id: ${productId}`);

          return false;
        }
        return (
          <ReservationCard
            key={productId}
            product={product}
            reservations={reservations[productId]}
            features={features}
            loadingFeatures={loadingFeatures}
            selectedFeatureRefs={selectedFeatureRefs[productId]}
            setSelectedFeatureRefs={features => {
              setSelectedFeatureRefs(prevSelections => ({
                ...prevSelections,
                [productId]: features,
              }));
            }}
            onZoomToFeature={setZoomedFeature}
            collectionId={collectionId}
          />
        );
      })}
    </Stack>
  );
};

CompletedTabContent.propTypes = {
  reservations: object,
  productCount: number,
  products: array,
  features: array,
  loadingFeatures: bool,
  selectedFeatureRefs: object,
  setSelectedFeatureRefs: func,
  setZoomedFeature: func,
  mode: string,
  collectionId: string,
  loadingReservations: bool,
};

export default CompletedTabContent;
